import ConfirmDeleteModal from '@/components/confirm-delete';
import {Text, TouchableOpacity, View} from '@/components/react-native';
import {auth} from '@/config/firebase';
import {updateNoteForAirportNote} from '@/firebase/airport-note';
import {ENoteCategory, ENoteTag, ENoteType, INoteInfo} from '@/types/airport';
import classNames from 'classnames';
import moment from 'moment';
import {useEffect, useState} from 'react';

interface NoteDetailFormProps {
  id?: string;
  initialContent?: string;
  initialTags?: ENoteTag[];
  category?: ENoteCategory;
  type?: ENoteType;
  airportName?: string;
  airportCode?: string;
  noteData?: INoteInfo;
  onSave: (content: string, summary: string, tags: ENoteTag[]) => void;
  onCancel: () => void;
  onDelete: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

export const NoteDetailForm = ({
  id,
  initialContent = '',
  initialTags = [],
  category = ENoteCategory.DEPARTURE,
  type = ENoteType.PROCEDURAL_NOTES,
  airportName = '[Airport Name]',
  airportCode = '[IATA]/[ICAO]',
  noteData,
  onSave,
  onCancel,
  onDelete,
  onApprove,
  onReject
}: NoteDetailFormProps) => {
  const [content, setContent] = useState(initialContent);
  const [selectedTags, setSelectedTags] = useState(initialTags);
  const [showDelete, setShowDelete] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showApprovalSection, setShowApprovalSection] = useState(true);

  // Set current user on component mount
  useEffect(() => {
    if (auth.currentUser) {
      setCurrentUserId(auth.currentUser.uid);
    }
  }, []);

  const handleApprove = async () => {
    if (!noteData || !currentUserId) return;

    setLoading(true);
    try {
      const currentTime = moment().format('YYYY-MM-DD HH:mm:ss');
      const updateData: Partial<INoteInfo> = {
        approved_by: 'Admin',
        approved_at: currentTime,
        updated_at: currentTime,
        is_new: true,
        rejected_by: '',
        rejected_at: ''
      };

      await updateNoteForAirportNote(noteData.airport, noteData.id, updateData);

      // Also update current station's updated_at
      // This would need to be implemented based on your station data structure

      if (onApprove) {
        onApprove();
      }
      setShowApprovalSection(false);
    } catch (error) {
      console.error('Error approving note:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!noteData || !currentUserId) return;

    setLoading(true);
    try {
      const currentTime = moment().format('YYYY-MM-DD HH:mm:ss');
      const updateData: Partial<INoteInfo> = {
        rejected_by: 'Admin',
        rejected_at: currentTime,
        updated_at: currentTime,
        approved_by: '',
        approved_at: ''
      };

      await updateNoteForAirportNote(noteData.airport, noteData.id, updateData);

      if (onReject) {
        onReject();
      }
      setShowApprovalSection(false);
    } catch (error) {
      console.error('Error rejecting note:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (content.trim()) {
      onSave(content.trim(), '', selectedTags);
    }
  };

  const getSectionName = () => {
    console.log('category', category);
    switch (category) {
      case ENoteCategory.ARRIVAL:
        return 'Arrival';
      case ENoteCategory.DEPARTURE:
        return 'Departure';
      case ENoteCategory.GROUND_OPERATIONS:
        return 'Ground Operations';
      case ENoteCategory.RESTRICTIONS:
        return 'Restrictions';
      case ENoteCategory.CAUTION:
        return 'Caution';
      default:
        return 'Departure';
    }
  };

  const getSubSectionName = () => {
    switch (type) {
      case ENoteType.PROCEDURAL_NOTES:
        return 'Procedural Notes';
      case ENoteType.OTHER_NOTES:
        return 'Other Notes';
      default:
        return 'Procedural Notes';
    }
  };

  return (
    <View className="bg-[#090024] w-full flex-col items-center flex-grow">
      <View className="flex-row items-center justify-between py-[16px] w-full">
        <TouchableOpacity onPress={onCancel} className="px-[16px]">
          <Text
            fontFamily="Geist"
            className="text-[#38BDF8] text-[16px] font-[400] tracking-[-0.32px]"
          >
            Cancel
          </Text>
        </TouchableOpacity>

        <View className="flex-row items-center justify-center">
          <Text
            fontFamily="Geist"
            className="text-[#E7E5E4] text-[16px] font-[600] tracking-[-0.32px] capitalize"
          >
            {id === 'new-note' ? 'Add Note' : 'Edit Note'}
          </Text>
        </View>

        <TouchableOpacity
          onPress={handleSave}
          disabled={!content.trim()}
          className="px-[16px]"
        >
          <Text
            fontFamily="Geist"
            className={classNames('text-[16px] font-[500] tracking-[-0.32px]', {
              'text-[#38BDF8]': content.trim(),
              'text-[#44403C]': !content.trim()
            })}
          >
            {id === 'new-note' ? 'Add  ' : 'Save  '}&nbsp;&nbsp;
          </Text>
        </TouchableOpacity>
      </View>

      <View className="flex-grow w-full">
        {/* Admin Approval/Rejection Section */}
        {noteData &&
          id !== 'new-note' &&
          noteData.created_by !== 'Admin' &&
          showApprovalSection && (
            <View className="flex-col items-center justify-center py-[16px] px-[16px] gap-[12px]">
              {/* Show current status if approved or rejected */}
              {noteData.approved_by && (
                <View className="bg-[#065F464D] p-[12px] rounded-lg w-full">
                  <Text
                    fontFamily="Geist"
                    className="text-[#10B981] text-[16px] font-[500] tracking-[-0.32px] leading-[20px] text-center"
                  >
                    ✓ Approved by {noteData.approved_by} on{' '}
                    {new Date(noteData.approved_at).toLocaleDateString()}
                  </Text>
                </View>
              )}

              {noteData?.rejected_by && noteData?.rejected_at && (
                <View className="bg-[#7F1D1D4D] p-[12px] rounded-lg w-full">
                  <Text
                    fontFamily="Geist"
                    className="text-[#EF4444] text-[16px] font-[500] tracking-[-0.32px] leading-[20px] text-center"
                  >
                    ✗ Rejected by {noteData.rejected_by} on{' '}
                    {new Date(noteData.rejected_at).toLocaleDateString()}
                  </Text>
                </View>
              )}

              {/* Show action buttons if not yet approved or rejected */}
              {!noteData?.approved_by && !noteData?.rejected_by && (
                <>
                  <View className="flex-row gap-[12px] w-full">
                    <TouchableOpacity
                      onPress={handleApprove}
                      disabled={loading}
                      className="flex-1 bg-[#065F464D] p-[12px] rounded-lg"
                    >
                      <Text
                        fontFamily="Geist"
                        className={classNames(
                          'text-[16px] font-[500] tracking-[-0.32px] leading-[20px] text-center',
                          loading ? 'text-[#44403C]' : 'text-[#10B981]'
                        )}
                      >
                        {loading ? '...' : 'Approve'}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={handleReject}
                      disabled={loading}
                      className="flex-1 bg-[#7F1D1D4D] p-[12px] rounded-lg"
                    >
                      <Text
                        fontFamily="Geist"
                        className={classNames(
                          'text-[16px] font-[500] tracking-[-0.32px] leading-[20px] text-center',
                          loading ? 'text-[#44403C]' : 'text-[#EF4444]'
                        )}
                      >
                        {loading ? '...' : 'Reject'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>
          )}

        <View className="px-[16px] py-[18px]">
          <Text
            fontFamily="GeistMono"
            className="text-[#FAFAF9] text-[16px] font-[500] tracking-[-0.32px] uppercase"
          >
            NOTE LOCATION
          </Text>
        </View>

        <View className="flex-col gap-[16px] items-start justify-start">
          {/* Airport Info Row */}
          <View className="flex-row items-center justify-center w-full px-[16px]">
            <View className="flex-1 w-[204px]">
              <Text
                fontFamily="GeistMono"
                className="text-[#D6D3D1] text-left text-[14px] font-[500] tracking-[-0.28px] mb-[6px]"
              >
                AIRPORT
              </Text>
              <Text
                fontFamily="Geist"
                className="text-[#FAFAF9] text-[20px] font-[500] tracking-[-0.4px]"
              >
                {airportName}
              </Text>
            </View>
            <View className="flex-1 w-[204px]">
              <Text
                fontFamily="GeistMono"
                className="text-[#D6D3D1] text-[14px] font-[500] tracking-[-0.28px] mb-[6px]"
              >
                AIRPORT CODE
              </Text>
              <Text
                fontFamily="Geist"
                className="text-[#FAFAF9] text-[20px] font-[500] tracking-[-0.4px]"
              >
                {airportCode}
              </Text>
            </View>
          </View>

          <View className="flex-row items-center justify-center w-full px-[16px]">
            <View className="flex-1 w-[204px]">
              <Text
                fontFamily="GeistMono"
                className="text-[#D6D3D1] text-[14px] font-[500] tracking-[-0.28px] mb-[6px]"
              >
                SECTION
              </Text>
              <Text
                fontFamily="Geist"
                className="text-[#FAFAF9] text-[20px] font-[500] tracking-[-0.4px]"
              >
                {getSectionName()}
              </Text>
            </View>
          </View>
        </View>

        <View className="px-[16px] py-[18px] mt-[24px]">
          <Text
            fontFamily="GeistMono"
            className="text-[#FAFAF9] text-[16px] font-[500] tracking-[-0.32px] uppercase"
          >
            NOTE CONTENT
          </Text>
        </View>

        {/* Note Content Section */}
        <View className="px-[16px]">
          <View className="bg-[#010009] w-full py-[12px] px-[16px] rounded-[8px]">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write comment"
              className="text-[#FAFAF9] bg-transparent w-full outline-none min-h-[200px]"
            />
          </View>
        </View>
        {id !== 'new-note' && (
          <View className="flex-col items-center justify-center mt-[24px] py-[16px] px-[16px]">
            <TouchableOpacity
              onPress={() => setShowDelete(true)}
              className="bg-[#7F1D1D4D] p-[12px] rounded-lg w-full"
            >
              <Text
                fontFamily="Geist"
                className="text-[#DC2626] text-[16px] font-[500] tracking-[-0.32px] leading-[20px] text-center"
              >
                Delete Note
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      {showDelete && (
        <ConfirmDeleteModal
          title="Confirm Delete"
          description="Please note that this action cannot be undone."
          onClose={() => setShowDelete(false)}
          onConfirm={() => {
            setShowDelete(false);
            onDelete();
          }}
        />
      )}
    </View>
  );
};
